/**
 * AddressAutocomplete — Google Places-powered address input.
 *
 * Uses the modern PlaceAutocompleteElement web component (Places API New —
 * required for any GCP project created after March 1 2025) and falls back
 * to the legacy google.maps.places.Autocomplete for older projects.
 *
 * Host page must already be wrapped in <APIProvider> (added in App.js).
 */
import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";

export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Start typing an address…",
  countryRestriction = "au",
  className = "",
  testId,
}) {
  const places = useMapsLibrary("places");
  const containerRef = useRef(null);
  const elRef = useRef(null);
  const inputRef = useRef(null);
  const legacyAcRef = useRef(null);
  const [useLegacy, setUseLegacy] = useState(false);

  // Keep latest callbacks in refs so the effect doesn't reinit the WC
  // every render (which would lose the user's typed text + selection).
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const valueRef = useRef(value);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);
  useEffect(() => { valueRef.current = value; }, [value]);

  // ─── Modern PlaceAutocompleteElement path ──────────────────────────────
  useEffect(() => {
    if (!places || !containerRef.current || elRef.current || legacyAcRef.current) return;
    if (!places.PlaceAutocompleteElement) {
      setUseLegacy(true);
      return;
    }
    try {
      const el = new places.PlaceAutocompleteElement({
        includedRegionCodes: countryRestriction ? [countryRestriction] : undefined,
      });
      el.style.width = "100%";
      el.style.colorScheme = "light";
      if (testId) el.setAttribute("data-testid", testId);

      // Prefill with the already-saved address (the WC starts empty).
      const initial = valueRef.current;
      if (initial) {
        requestAnimationFrame(() => {
          try {
            const inp = el.shadowRoot?.querySelector("input") || el.querySelector("input");
            if (inp && !inp.value) {
              inp.value = initial;
            }
          } catch { /* shadow DOM closed — fall back to the Saved line below */ }
        });
      }

      el.addEventListener("gmp-select", async (ev) => {
        try {
          const placePrediction = ev.placePrediction;
          if (!placePrediction) return;
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ["formattedAddress", "location", "id"] });
          const addr = place.formattedAddress || "";
          const loc = place.location;
          if (!loc) return;
          onChangeRef.current?.(addr);
          onPlaceSelectedRef.current?.({
            address: addr,
            lat: loc.lat(),
            lng: loc.lng(),
            place_id: place.id || "",
          });
        } catch (e) {
          console.warn("Place select error", e);
        }
      });

      containerRef.current.appendChild(el);
      elRef.current = el;
    } catch (e) {
      console.warn("PlaceAutocompleteElement failed, falling back to legacy", e);
      setUseLegacy(true);
    }

    return () => {
      try { elRef.current?.remove(); } catch { /* noop */ }
      elRef.current = null;
    };
  }, [places, countryRestriction, testId]);

  // ─── Legacy fallback — google.maps.places.Autocomplete on a real input ─
  useEffect(() => {
    if (!useLegacy || !places || !inputRef.current || legacyAcRef.current) return;
    if (!places.Autocomplete) return;
    try {
      const ac = new places.Autocomplete(inputRef.current, {
        fields: ["formatted_address", "geometry.location", "place_id", "name"],
        componentRestrictions: countryRestriction ? { country: countryRestriction } : undefined,
      });
      legacyAcRef.current = ac;
      const l = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const addr = place?.formatted_address || place?.name || "";
        const loc = place?.geometry?.location;
        if (!loc) return;
        onChangeRef.current?.(addr);
        onPlaceSelectedRef.current?.({
          address: addr,
          lat: loc.lat(),
          lng: loc.lng(),
          place_id: place.place_id || "",
        });
      });
      return () => { try { l.remove(); } catch { /* noop */ } };
    } catch {
      /* noop */
    }
  }, [useLegacy, places, countryRestriction]);

  if (useLegacy) {
    return (
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={containerRef} className="gmp-pac-wrap [&_gmp-place-autocomplete]:block [&_gmp-place-autocomplete]:w-full" />
      {!places && (
        <Input value={value || ""} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} data-testid={testId} />
      )}
      {places && value ? (
        <p className="mt-1 text-[11px] text-neutral-600 truncate" data-testid={testId ? `${testId}-saved` : undefined}>
          <span className="font-semibold">Saved:</span> {value}
        </p>
      ) : null}
    </div>
  );
}
